import { track } from '@/common/track';
import { CreateStudentDialog } from '@/components/dialogs/CreateStudentDialog';
import { EnrolledStudentsDialog } from '@/components/dialogs/EnrolledStudentsDialog';
import { TrainingBalanceInfoDialog } from '@/components/dialogs/TrainingBalanceInfoDialog';
import { GroupByMultiSelector } from '@/components/selectors/GroupByMultiSelector';
import { MultiSelector } from '@/components/selectors/MultiSelector';
import { DEFAULT_PAGE, MAX_PAGE_SIZE } from '@/constants';
import { queryKeys } from '@/hooks/queryKeys';
import { useCheckoutEnrolments } from '@/hooks/useCheckoutEnrolments';
import useDebounce from '@/hooks/useDebounce';
import { useEnrolStudents } from '@/hooks/useEnrolStudents';
import { useEnrolStudentsInOwnPortal } from '@/hooks/useEnrolStudentsInOwnPortal';
import { useGetEnrollableCourses } from '@/hooks/useGetEnrollableCourses';
import { useGetEnrollableStudents } from '@/hooks/useGetEnrollableStudents';
import { useGetEnrolmentPreview } from '@/hooks/useGetEnrolmentPreview';
import { useLocale } from '@/providers/LocaleProvider';
import { showGlobalSnackbar } from '@/providers/SnackbarProvider';
import BookOpenIcon from '@/resources/images/icons-book-open.svg';
import CloseIcon from '@/resources/images/icons-close.svg';
import UsersIcon from '@/resources/images/icons-users.svg';
import WarningIcon from '@/resources/images/icons-warning.svg';
import { KLAYO_COLORS } from '@/themes';
import { BaseSelectorOption } from '@/types';
import { IEnrollableCourse } from '@/types/views/enrollment.types';
import { ITrainingBalance } from '@/types/views/trainingManagerConfiguration';
import { formatCurrency } from '@/utilities/formatCurrency';
import styled from '@emotion/styled';
import {
  Alert,
  AppBar,
  Box,
  DialogContent,
  IconButton,
  Link,
  ListSubheader,
  Dialog as MuiDialog,
  Paper,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { ConfirmationDialog, ManageTrainingEvent } from 'klayowebshared';
import { isEmpty } from 'lodash';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EnrolSummaryPanel, PaymentOption } from './EnrolSummaryPanel';

// Card-checkout caps, mirroring the server's (one cart line's quantity cap and the participants
// attribute's all-or-nothing limit). The server enforces both; these only pre-disable the option.
const MAX_CART_STUDENTS = 100;
const MAX_CART_ENROLMENTS = 200;
// The server's cap on courses × students for one own-portal enrolment: it enrols pair by pair
// against LearnUpon, so a request stays well below the catalogue's batch.
const MAX_OWN_PORTAL_PAIRS = 20;

// The portal a course is enrolled through: its own-portal integration id, or null for the catalogue.
// One enrolment holds courses of one portal only.
const portalOf = (course: IEnrollableCourse) =>
  course.source === 'ownPortal' ? course.integrationId : null;

interface CourseOption {
  id: string;
  name: string;
  group: string;
  portal: string | null;
  // Read by the selector's option row to show "name | ABBR".
  organizationIntegrationAbbreviation: string | null;
}

const toCourseOption = (c: IEnrollableCourse, catalogueGroup: string): CourseOption => ({
  id: c.courseId,
  name: c.title,
  group: c.source === 'ownPortal' ? (c.integrationName ?? '') : catalogueGroup,
  portal: portalOf(c),
  organizationIntegrationAbbreviation: c.integrationAbbreviation || c.integrationName,
});

// --- Component ---

interface Props {
  amount?: number;
  isUnlimitedCredit?: boolean;
  balances?: ITrainingBalance[];
}

export const EnrolStudents = ({ amount, isUnlimitedCredit, balances }: Props) => {
  const { translate } = useLocale();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const courseIds = useMemo(() => searchParams.getAll('courseId'), [searchParams]);
  const studentIds = useMemo(() => searchParams.getAll('employeeId'), [searchParams]);

  useEffect(() => {
    track(ManageTrainingEvent.EnrolViewed, {
      coursesPreSelected: courseIds.length,
      studentCountPreSelected: studentIds.length,
      hasPrePaidBalance: (amount ?? 0) > 0 || !!isUnlimitedCredit,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { mutate: enrolStudents, isPending: isEnrolling } = useEnrolStudents();
  const { mutate: enrolStudentsInOwnPortal, isPending: isEnrollingOwnPortal } =
    useEnrolStudentsInOwnPortal();
  const { onCheckoutEnrolments, isPending: isCheckingOut } = useCheckoutEnrolments();
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const [selectedCourses, setSelectedCourses] = useState<IEnrollableCourse[]>([]);
  const [selectedStudents, setSelectedStudents] = useState<BaseSelectorOption[]>([]);
  const [paymentOption, setPaymentOption] = useState<PaymentOption>('useBalance');
  const [studentSearch, setStudentSearch] = useState('');
  const [showCreateStudent, setShowCreateStudent] = useState(false);
  const [newStudentName, setNewStudentName] = useState('');
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [showConflictDetails, setShowConflictDetails] = useState(false);
  const studentSearchDebounce = useDebounce(studentSearch.trim(), 300);

  const selectedCourseIds = useMemo(
    () => selectedCourses.map((c) => c.courseId),
    [selectedCourses]
  );
  const selectedStudentIds = useMemo(
    () => selectedStudents.map((s) => s.value as string),
    [selectedStudents]
  );

  // The selection's own-portal course, if any. Own-portal courses are free (no balance, no card) and
  // enrolled through their own endpoint, so this is checked BEFORE isFreeOrder everywhere: they also
  // carry `free`, and without this branch would be posted to the catalogue enrol endpoint.
  const ownPortalCourse = selectedCourses.find((c) => c.source === 'ownPortal');
  const isOwnPortal = !!ownPortalCourse;
  const ownPortalName = ownPortalCourse?.integrationName ?? '';

  // What each (student, course) pair would actually do. Advisory only: it feeds the warning below so
  // the TM can review who already holds an enrolment. Nothing here excludes a pair or blocks the
  // button — whether to go ahead anyway is the TM's call, not this dialog's.
  const { data: previewData } = useGetEnrolmentPreview({
    courseIds: isOwnPortal ? [] : selectedCourseIds,
    integrationCourseIds: isOwnPortal ? selectedCourseIds : [],
    employeeIds: selectedStudentIds,
  });

  // By action, not `billable`: every own-portal pair is unbillable, including new ones.
  const alreadyEnrolledPairs = useMemo(
    () => previewData?.pairs.filter((p) => p.action === 'alreadyHasAccess') ?? [],
    [previewData]
  );

  // Every selected (student, course) pair, the already-enrolled ones included — they are flagged by
  // the warning above rather than removed from the order. Note the server still drops a pair the
  // student already holds live access to, so for those pairs this quotes more than it will charge.
  const totals = useMemo(() => {
    const seats = selectedStudents.length;
    let count = 0;
    let cost = 0;
    let discount = 0;

    selectedCourses.forEach((c) => {
      count += seats;
      const price = c.priceInCents ?? 0;
      cost += price * seats;
      discount += price * (c.discountPercent / 100) * seats;
    });

    return { count, cost, discount };
  }, [selectedCourses, selectedStudents]);

  const enrolmentsCount = totals.count;
  const totalCost = totals.cost;
  const totalDiscountAmount = totals.discount;

  const balanceAfterEnrollment = amount ? amount - (totalCost - totalDiscountAmount) : 0;
  // Something is selected. A student who already holds the course no longer suppresses this: they
  // are warned about above and counted like any other, so the button stays available.
  const hasSelections = selectedCourses.length > 0 && selectedStudents.length > 0;

  // Nothing to pay: every selected course is free. Neither payment option applies — there is no
  // balance to draw down and the server refuses a cart of $0 lines alone — so the enrol endpoint
  // takes the order outright. A $0 course reads `purchasable` so it can ride in a cart beside a
  // paid one; this branch is what keeps an all-free selection out of the cart.
  const isFreeOrder = hasSelections && selectedCourses.every((c) => c.free);

  // Balance can only cover the order when every selected course is credit-eligible AND there is
  // enough balance (or unlimited credit). Any non-credit-eligible course forces Add to cart.
  const canUseBalance =
    isFreeOrder ||
    (selectedCourses.every((c) => c.creditEligible) &&
      (balanceAfterEnrollment > 0 || isUnlimitedCredit));

  // Card checkout is only offered when the server will accept it: every selected course must be
  // purchasable (Purchasable is server-computed to mirror the checkout endpoint's rules) and the
  // selection must fit the cart caps.
  const nonPurchasableCourses = useMemo(
    () => selectedCourses.filter((c) => !c.purchasable),
    [selectedCourses]
  );
  // The full cross-product, which is what the server caps at step 2 of the checkout.
  const withinCartLimits =
    selectedStudents.length <= MAX_CART_STUDENTS &&
    selectedCourses.length * selectedStudents.length <= MAX_CART_ENROLMENTS;
  const canAddToCart = nonPurchasableCourses.length === 0 && withinCartLimits;
  const bothOptionsUnavailable = hasSelections && !canUseBalance && !canAddToCart;
  const ownPortalLimitExceeded = isOwnPortal && enrolmentsCount > MAX_OWN_PORTAL_PAIRS;

  // Keep the selected option on an AVAILABLE card: never auto-flip onto a disabled one, and flip
  // back off a card that just became disabled (e.g. an LP course was added while addToCart was
  // selected). When both are unavailable the selection stays put and the button is disabled.
  // Layout effect so the flip lands before paint — a plain effect leaves one painted frame where
  // the button still shows the just-disabled option's action.
  useLayoutEffect(() => {
    if (!enrolmentsCount) return;
    if (paymentOption === 'useBalance' && !canUseBalance && canAddToCart) {
      setPaymentOption('addToCart');
    } else if (paymentOption === 'addToCart' && !canAddToCart && canUseBalance) {
      setPaymentOption('useBalance');
    }
  }, [canUseBalance, canAddToCart, paymentOption, enrolmentsCount]);

  const { data: studentsData, isFetching: isStudentsFetching } = useGetEnrollableStudents({
    searchDebounce: studentSearchDebounce,
    paginationModel: {
      page: DEFAULT_PAGE,
      pageSize: MAX_PAGE_SIZE,
    },
  });

  const { data: enrollableCoursesData, isLoading: isEnrollableCoursesLoading } =
    useGetEnrollableCourses();

  const catalogueGroup = translate('manage.enrolStudentsPage.klayoCatalogue');
  const courseOptions = useMemo(() => {
    if (!enrollableCoursesData?.courses) return [];
    return (
      enrollableCoursesData.courses
        .filter((c) => !selectedCourses.some((sc) => sc.courseId === c.courseId))
        .map((c) => toCourseOption(c, catalogueGroup))
        // Grouping needs each group's options together: own portals first, the catalogue last. The
        // sort is stable, so the server's title order holds within a group.
        .sort(
          (a, b) =>
            Number(a.portal === null) - Number(b.portal === null) || a.group.localeCompare(b.group)
        )
    );
  }, [enrollableCoursesData, selectedCourses, catalogueGroup]);

  // One portal per selection: once a course is picked, every other portal's courses are disabled.
  const selectedPortal = selectedCourses.length ? portalOf(selectedCourses[0]) : undefined;
  const isCourseOptionDisabled = (option: CourseOption) =>
    selectedPortal !== undefined && option.portal !== selectedPortal;
  const selectedGroup = selectedCourses.length
    ? toCourseOption(selectedCourses[0], catalogueGroup).group
    : '';

  const studentOptions = useMemo(() => {
    return (
      studentsData?.students.data
        ?.map((student) => ({
          value: student.employeeId,
          label: student.fullName,
          email: student.email,
          isNameUnique: student.isNameUnique,
        }))
        ?.filter((student) => !selectedStudents.some((s) => s.value === student.value)) ?? []
    );
  }, [studentsData]);

  useEffect(() => {
    if (!courseIds.length || !enrollableCoursesData?.courses) return;
    const matched = enrollableCoursesData.courses.filter((c) => courseIds.includes(c.courseId));
    if (!matched.length) return;
    // A hand-made URL can name courses of several portals: keep the portal of the first one named.
    const firstNamed = courseIds
      .map((id) => matched.find((c) => c.courseId === id))
      .find(Boolean) as IEnrollableCourse;
    setSelectedCourses(matched.filter((c) => portalOf(c) === portalOf(firstNamed)));
  }, [courseIds, enrollableCoursesData]);

  useEffect(() => {
    if (!studentIds.length || isEmpty(studentOptions)) return;
    const preselected = studentOptions.filter((s) => studentIds.includes(s.value as string));
    if (preselected.length) setSelectedStudents(preselected);
  }, [studentIds, studentOptions]);

  const buttonLabel = useMemo(() => {
    // Nothing is paid for on an own portal, so no payment wording, not even "for free".
    if (isOwnPortal) return translate('manage.enrolStudentsPage.enrol');
    // With no selection, or both payment options unavailable, the button is disabled — show the
    // neutral label rather than an action naming a card the user cannot use.
    if (!hasSelections || bothOptionsUnavailable)
      return translate('manage.enrolStudentsPage.proceedToPayment');
    // No payment is involved in a free order, so "Enrol with balance" would name a balance that is
    // neither used nor required.
    if (isFreeOrder) return translate('manage.enrolStudentsPage.enrolForFree');
    return paymentOption === 'useBalance'
      ? translate('manage.enrolStudentsPage.enrolWithBalance')
      : translate('manage.enrolStudentsPage.addToCart');
  }, [isOwnPortal, hasSelections, bothOptionsUnavailable, isFreeOrder, paymentOption, translate]);

  const handleClose = () => {
    navigate(-1);
  };

  const handleRemoveCourse = (courseId: string) => {
    setSelectedCourses((prev) => prev.filter((c) => c.courseId !== courseId));
  };

  const [showEnrolConfirm, setShowEnrolConfirm] = useState(false);

  const onProceedToPayment = () => {
    if (!hasSelections) return;
    if (isOwnPortal) {
      if (!ownPortalLimitExceeded) setShowEnrolConfirm(true);
      return;
    }
    // A free order goes straight to the enrol endpoint. This is the ONLY guard keeping an all-free
    // selection out of the cart: a $0 course reads `purchasable`, so paymentOption can still be
    // 'addToCart' (e.g. after removing the paid half of a mixed selection). Falling through would
    // post a cart of $0 lines, which the checkout endpoint refuses.
    if (isFreeOrder) {
      setShowEnrolConfirm(true);
      return;
    }
    if (paymentOption === 'useBalance') {
      if (!canUseBalance) return;
      setShowEnrolConfirm(true);
      return;
    }

    if (!canAddToCart || isCheckingOut) return;
    const eventPayload = {
      coursesSelected: selectedCourses.length,
      studentCount: selectedStudents.length,
      total$: totalCost / 100,
    };
    track(ManageTrainingEvent.EnrolAddToCart, eventPayload);
    setCheckoutError(null);
    onCheckoutEnrolments(
      // The SAME arrays the preview priced — deriving them again here is how a quote and a charge
      // drift apart.
      { courseIds: selectedCourseIds, employeeIds: selectedStudentIds },
      {
        onSuccess: (res) => {
          // window.open returns null when a popup blocker eats the tab. The checkout URL exists
          // nowhere else (nothing is persisted until Shopify's orders/paid webhook), so fall back
          // to same-tab navigation instead of closing the dialog on a tab that never opened.
          const checkoutTab = window.open(res.data.checkoutUrl, '_blank');
          track(ManageTrainingEvent.EnrolAddToCartSuccess, {
            ...eventPayload,
            popupBlocked: !checkoutTab,
          });
          if (checkoutTab) navigate(-1);
          else window.location.assign(res.data.checkoutUrl);
        },
        onError: (error: any) => {
          const message = error?.response?.data?.message || error?.message;
          track(ManageTrainingEvent.EnrolAddToCartFail, { reason: message || 'unknown' });
          setCheckoutError(message || translate('manage.enrolStudentsPage.checkoutFailed'));
        },
      }
    );
  };

  const onConfirmEnrolOwnPortal = () => {
    const eventPayload = {
      coursesSelected: selectedCourses.length,
      studentCount: selectedStudents.length,
      source: 'ownPortal',
    };
    enrolStudentsInOwnPortal(
      { integrationCourseIds: selectedCourseIds, employeeIds: selectedStudentIds },
      {
        onSuccess: (res) => {
          const { enrolled, skipped, failed } = res.data;
          track(ManageTrainingEvent.EnrolEnrolSuccess, {
            ...eventPayload,
            enrolled: enrolled.length,
            skipped: skipped.length,
            failed: failed.length,
          });
          // Pending enrolment requests get approved. There is no Klayo enrolment row until the
          // integration sync, and no balance to refresh.
          queryClient.invalidateQueries({ queryKey: queryKeys.courseStudents.key });
          queryClient.invalidateQueries({ queryKey: queryKeys.feedbackCourseRequests.key });
          setShowEnrolConfirm(false);
          navigate(-1);
          // Not all or nothing: the pairs already enrolled stay enrolled, so report the rest.
          if (failed.length > 0) {
            showGlobalSnackbar(
              translate('manage.enrolStudentsPage.ownPortalPartialFailure', {
                enrolled: enrolled.length,
                failed: failed.length,
                reason: translate(
                  `manage.enrolStudentsPage.ownPortalFailureReasons.${failed[0].reason ?? 'unreachable'}`
                ),
              }),
              'warning'
            );
          }
        },
        onError: (error: any) => {
          track(ManageTrainingEvent.EnrolEnrolFail, {
            source: 'ownPortal',
            reason: error?.response?.data?.message || error?.message || 'unknown',
          });
        },
      }
    );
  };

  const onConfirmEnrol = () => {
    const eventPayload = {
      coursesSelected: selectedCourses.length,
      studentCount: selectedStudents.length,
      total$: (totalCost - totalDiscountAmount) / 100,
    };
    track(ManageTrainingEvent.EnrolEnrolWithBalance, eventPayload);
    enrolStudents(
      { courseIds: selectedCourseIds, employeeIds: selectedStudentIds },
      {
        onSuccess: () => {
          track(ManageTrainingEvent.EnrolEnrolSuccess, eventPayload);
          queryClient.invalidateQueries({
            queryKey: queryKeys.trainingBalance.balance(),
          });
          setShowEnrolConfirm(false);
          navigate(-1);
        },
        onError: (error: any) => {
          track(ManageTrainingEvent.EnrolEnrolFail, {
            reason: error?.response?.data?.message || error?.message || 'unknown',
          });
        },
      }
    );
  };

  return (
    <>
      <MuiDialog open fullScreen>
        {/* Container */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            bgcolor: KLAYO_COLORS.GreyGR050,
          }}
        >
          {/* Header AppBar */}
          <AppBar
            position='static'
            elevation={0}
            sx={{
              bgcolor: '#fff',
            }}
          >
            <Toolbar sx={{ px: { xs: 2, md: 5 }, py: 1 }}>
              <Typography
                variant='h5'
                sx={{ flexGrow: 1, fontWeight: 700, color: KLAYO_COLORS.GreyGR900 }}
              >
                {translate('manage.enrolStudentsPage.title')}
              </Typography>
              <IconButton onClick={handleClose}>
                <StyledCloseIcon width={24} height={24} color={KLAYO_COLORS.NeonBlueNB500} />
              </IconButton>
            </Toolbar>
          </AppBar>

          {/* Scrollable Content */}
          <DialogContent
            sx={{ bgcolor: KLAYO_COLORS.WhiteWH900, flexGrow: 1, p: 0, overflow: 'auto' }}
          >
            {/* Content Wrapper */}
            <Box
              sx={{
                maxWidth: 1200,
                mx: 'auto',
                p: { xs: 2, md: 5 },
                display: 'flex',
                gap: 5,
                flexDirection: { xs: 'column', md: 'row' },
              }}
            >
              {/* Left - Form */}
              <Stack gap={5} flex={1}>
                <Stack gap={3}>
                  {/* Courses Section */}
                  <Typography variant='h6' fontSize={'18px'} fontWeight={600}>
                    {translate('manage.enrolStudentsPage.courses')}
                  </Typography>

                  <GroupByMultiSelector<CourseOption>
                    options={courseOptions}
                    value={selectedCourses.map((c) => toCourseOption(c, catalogueGroup))}
                    // Picked courses are shown as cards below.
                    isShowValue={false}
                    placeHolder={translate('manage.enrolStudentsPage.selectCourses')}
                    translate={translate}
                    getOptionLabel={(option) => option.name}
                    getOptionId={(option) => option.id}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    groupBy={(option) => option.group}
                    getOptionDisabled={isCourseOptionDisabled}
                    renderGroup={(params) => (
                      <li key={params.key}>
                        <ListSubheader component='div'>{params.group}</ListSubheader>
                        {selectedPortal !== undefined && params.group !== selectedGroup && (
                          <Typography
                            variant='caption'
                            color={KLAYO_COLORS.GreyGR500}
                            display='block'
                            px={2}
                            pb={1}
                          >
                            {selectedPortal === null
                              ? translate('manage.enrolStudentsPage.catalogueOnlyHint')
                              : params.group === catalogueGroup
                                ? translate('manage.enrolStudentsPage.ownPortalOnlyHint')
                                : translate('manage.enrolStudentsPage.otherPortalHint', {
                                    portal: selectedGroup,
                                  })}
                          </Typography>
                        )}
                        <ul style={{ padding: 0 }}>{params.children}</ul>
                      </li>
                    )}
                    onChange={(_e, values) => {
                      const courses = values
                        .map((v) => enrollableCoursesData?.courses.find((c) => c.courseId === v.id))
                        .filter(Boolean) as IEnrollableCourse[];
                      courses
                        .filter((c) => !selectedCourses.some((sc) => sc.courseId === c.courseId))
                        .forEach((c) =>
                          track(ManageTrainingEvent.EnrolCourseAdded, {
                            courseName: c.title,
                            ...(c.source === 'ownPortal' && { source: 'ownPortal' }),
                          })
                        );
                      setSelectedCourses(courses);
                    }}
                    isLoading={isEnrollableCoursesLoading}
                  />

                  {selectedCourses.length > 0 ? (
                    <Stack gap={2}>
                      {selectedCourses.map((course) => (
                        <Paper
                          key={course.courseId}
                          variant='outlined'
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            p: '16px',
                            borderRadius: '16px',
                            borderColor: KLAYO_COLORS.NeonBlueNB100,
                          }}
                        >
                          <Stack gap={0.5} sx={{ flex: 1 }}>
                            <Typography variant='body1' color={KLAYO_COLORS.GreyGR900}>
                              {course.title}
                            </Typography>
                            {course.source === 'ownPortal' ? (
                              <Typography variant='caption' color={KLAYO_COLORS.GreyGR500}>
                                {course.integrationName}
                              </Typography>
                            ) : (
                              <Stack direction='row' alignItems='baseline' gap={0.5}>
                                <Typography
                                  variant='body1'
                                  fontWeight={600}
                                  color={KLAYO_COLORS.GreyGR900}
                                >
                                  {formatCurrency(course.priceInCents ?? 0, 2)}
                                </Typography>
                                <Typography variant='caption' color={KLAYO_COLORS.GreyGR500}>
                                  {translate('manage.enrolStudentsPage.perStudent')}
                                </Typography>
                              </Stack>
                            )}
                          </Stack>
                          <IconButton
                            size='small'
                            onClick={() => handleRemoveCourse(course.courseId)}
                          >
                            <StyledCloseIcon
                              width={24}
                              height={24}
                              color={KLAYO_COLORS.NeonBlueNB500}
                            />
                          </IconButton>
                        </Paper>
                      ))}
                    </Stack>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 5,
                        bgcolor: KLAYO_COLORS.NeonBlueNB025,
                        borderRadius: '16px',
                        textAlign: 'center',
                        gap: 2,
                      }}
                    >
                      <BookOpenIcon width={48} height={48} color={KLAYO_COLORS.NeonBlueNB200} />
                      <Stack gap={0.5}>
                        <Typography
                          variant='h6'
                          fontWeight={600}
                          color={KLAYO_COLORS.GreyGR900}
                          fontSize={18}
                        >
                          {translate('manage.enrolStudentsPage.noCoursesSelected')}
                        </Typography>
                        <Typography variant='body2' color={KLAYO_COLORS.GreyGR500}>
                          {translate('manage.enrolStudentsPage.noCoursesSelectedDesc')}
                        </Typography>
                      </Stack>
                    </Box>
                  )}
                </Stack>

                <Stack gap={3}>
                  {/* Students Section */}
                  <Typography variant='h6' fontSize={'18px'} fontWeight={600}>
                    {translate('manage.enrolStudentsPage.students')}
                  </Typography>

                  <MultiSelector<BaseSelectorOption>
                    label={null}
                    items={studentOptions}
                    values={selectedStudents}
                    placeHolder={translate('manage.enrolStudentsPage.selectStudents')}
                    // Every training manager may create a student. The "Create students" permission
                    // only decides, server-side, whether the student also gets an OLC account.
                    hasNewItem={true}
                    newItemLabel={translate('manage.createStudent.create')}
                    translate={translate}
                    onChange={(_e, values) => {
                      if (values.length > selectedStudents.length) {
                        track(ManageTrainingEvent.EnrolStudentAdded);
                      }
                      setSelectedStudents(values);
                    }}
                    getItemLabel={(item) => item.label ?? ''}
                    // Two enrollable students can share a full name; the email is the only thing
                    // that tells the manager which one they are about to enrol.
                    getItemDescription={(item) =>
                      item.isNameUnique === false ? item.email : undefined
                    }
                    onSearch={setStudentSearch}
                    isLoading={isStudentsFetching}
                    onBlur={() => setStudentSearch('')}
                    onNewItemClick={(_e, value) => {
                      setNewStudentName(value);
                      // Via track(), not Userpilot directly: track() is the single place that
                      // forwards to Userpilot, and it swallows telemetry failures. A raw
                      // Userpilot.track here could throw between the two setStates and leave the
                      // option looking dead because the dialog never opened.
                      track(ManageTrainingEvent.EnrolCreateStudentClicked);
                      setShowCreateStudent(true);
                    }}
                  />

                  {alreadyEnrolledPairs.length > 0 && (
                    <Alert
                      severity='warning'
                      // Not role="alert" (MUI's default): this settles again on every student added or
                      // removed, and an assertive live region would interrupt a screen reader each time.
                      role='status'
                      sx={{
                        bgcolor: KLAYO_COLORS.GoldGD050,
                        color: KLAYO_COLORS.GreyGR900,
                        alignItems: 'center',
                        borderRadius: '24px',

                        '& .MuiAlert-icon svg path[fill="#111827"]': {
                          fill: KLAYO_COLORS.GoldGD700,
                        },
                      }}
                      icon={<WarningIcon />}
                    >
                      <Typography variant='body1' color={KLAYO_COLORS.GreyGR900}>
                        {translate('manage.enrolStudentsPage.alreadyEnrolledSummary')}{' '}
                        <Link
                          component='button'
                          type='button'
                          underline='none'
                          onClick={() => setShowConflictDetails(true)}
                          sx={{
                            color: KLAYO_COLORS.NeonBlueNB500,
                            fontWeight: 500,
                            verticalAlign: 'baseline',
                          }}
                        >
                          {translate('manage.enrolStudentsPage.viewDetails')}
                        </Link>
                      </Typography>
                    </Alert>
                  )}

                  {selectedStudents.length === 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 5,
                        bgcolor: KLAYO_COLORS.NeonBlueNB025,
                        borderRadius: '16px',
                        textAlign: 'center',
                        gap: 2,
                      }}
                    >
                      <UsersIcon width={48} height={48} color={KLAYO_COLORS.NeonBlueNB200} />
                      <Stack gap={0.5}>
                        <Typography
                          variant='h6'
                          fontWeight={600}
                          color={KLAYO_COLORS.GreyGR900}
                          fontSize={18}
                        >
                          {translate('manage.enrolStudentsPage.noStudentsSelected')}
                        </Typography>
                        <Typography variant='body2' color={KLAYO_COLORS.GreyGR500}>
                          {translate('manage.enrolStudentsPage.noStudentsSelectedDesc')}
                        </Typography>
                      </Stack>
                    </Box>
                  )}
                </Stack>
              </Stack>

              {/* Right - Summary Panel */}
              <EnrolSummaryPanel
                coursesCount={selectedCourses.length}
                studentsCount={selectedStudents.length}
                enrolmentsCount={enrolmentsCount}
                hasSelections={hasSelections}
                isOwnPortal={isOwnPortal}
                ownPortalName={ownPortalName}
                ownPortalLimitMessage={
                  ownPortalLimitExceeded
                    ? translate('manage.enrolStudentsPage.ownPortalLimitExceeded', {
                        max: MAX_OWN_PORTAL_PAIRS,
                      })
                    : null
                }
                isFreeOrder={isFreeOrder}
                paymentOption={paymentOption}
                onPaymentOptionChange={setPaymentOption}
                canUseBalance={!!canUseBalance}
                canAddToCart={canAddToCart}
                amount={amount}
                isUnlimitedCredit={isUnlimitedCredit}
                onBalanceInfoClick={() => setInfoDialogOpen(true)}
                cartUnavailableReason={
                  nonPurchasableCourses.length > 0
                    ? translate('manage.enrolStudentsPage.notPurchasableOnline', {
                        titles: nonPurchasableCourses.map((c) => c.title).join(', '),
                      })
                    : translate('manage.enrolStudentsPage.cartLimitExceeded', {
                        maxStudents: MAX_CART_STUDENTS,
                        maxEnrolments: MAX_CART_ENROLMENTS,
                      })
                }
                totalCost={totalCost}
                totalDiscountAmount={totalDiscountAmount}
                checkoutError={checkoutError}
                onCheckoutErrorClose={() => setCheckoutError(null)}
                buttonLabel={buttonLabel}
                buttonDisabled={
                  isOwnPortal
                    ? !hasSelections || ownPortalLimitExceeded || isEnrollingOwnPortal
                    : !hasSelections || bothOptionsUnavailable || isCheckingOut
                }
                onButtonClick={onProceedToPayment}
              />
            </Box>
          </DialogContent>
        </Box>
      </MuiDialog>

      <CreateStudentDialog
        open={showCreateStudent}
        onClose={() => {
          setShowCreateStudent(false);
          setNewStudentName('');
        }}
        initialName={newStudentName}
        onCreated={(student) => {
          track(ManageTrainingEvent.EnrolStudentAdded);
          setSelectedStudents((prev) => [
            ...prev,
            { value: student.employeeId, label: student.fullName },
          ]);

          queryClient.invalidateQueries({ queryKey: queryKeys.enrollableStudents.list() });
        }}
      />

      {showEnrolConfirm && (
        <ConfirmationDialog
          title={translate('manage.enrolStudentsPage.confirmEnrolTitle')}
          question={translate(
            isOwnPortal
              ? 'manage.enrolStudentsPage.confirmEnrolQuestionOwnPortal'
              : isFreeOrder
                ? 'manage.enrolStudentsPage.confirmEnrolQuestionFree'
                : 'manage.enrolStudentsPage.confirmEnrolQuestion',
            {
              students: selectedStudents.length,
              studentLabel:
                selectedStudents.length === 1
                  ? translate('manage.enrolStudentsPage.student')
                  : translate('manage.enrolStudentsPage.studentsLabel'),
              courses: selectedCourses.length,
              courseLabel:
                selectedCourses.length === 1
                  ? translate('manage.enrolStudentsPage.course')
                  : translate('manage.enrolStudentsPage.coursesLabel'),
            }
          )}
          cancelButton={translate('common.cancel')}
          acceptButton={translate('manage.enrolStudentsPage.confirm')}
          loading={isOwnPortal ? isEnrollingOwnPortal : isEnrolling}
          onCancel={() => setShowEnrolConfirm(false)}
          onAccept={isOwnPortal ? onConfirmEnrolOwnPortal : onConfirmEnrol}
        />
      )}

      <TrainingBalanceInfoDialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        balances={balances || []}
        isUnlimitedCredit={isUnlimitedCredit}
      />

      <EnrolledStudentsDialog
        open={showConflictDetails}
        onClose={() => setShowConflictDetails(false)}
        pairs={alreadyEnrolledPairs}
      />
    </>
  );
};

const StyledCloseIcon = styled(CloseIcon)(() => ({
  '& path[fill="#111827"]': {
    fill: 'currentColor',
  },
}));
